import React, { useContext, useEffect, useState } from 'react';
import { Theme, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import useMediaQuery from '@mui/material/useMediaQuery';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import Slide from '@mui/material/Slide';
import { Link } from 'react-router-dom';
import PaymentIcon from '@mui/icons-material/Payment';
import classNames from 'classnames';
import HelpIcon from '@mui/icons-material/Help';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import useRouter from '../components/_hook/useRouter';
import HeaderAccountButton from '../components/_header/HeaderAccountButton';
import HeaderAppButton from './_module/HeaderAppButton';
import HeaderCountryButton from '../components/_header/HeaderCountryButton';
import HeaderCurrencyButton from '../components/_header/HeaderCurrencyButton';
import HeaderLanguageButton from '../components/_header/HeaderLanguageButton';
import HeaderCompanyButton from './_module/HeaderCompanyButton';
import DrawerHeader from './_module/DrawerHeader';
import useKeepitalRoutes from '../components/_hook/useKeepitalRoutes';
import KeepitalKeepitalAppSvg from '../components/_svg/KeepitalKeepitalAppSvg';
import Context from '../context';
import HeaderNotificationButton from './_module/HeaderNotificationButton';
import useRoutes from '../components/_hook/useRoutes';
import KEEPITAL_SUBSCRIPTION_PACKAGE from '../components/constant/keepitalSubscriptionPackage';
import Joyride from '../components/Joyride';

const drawerWidth = 240;
const responsiveBound = 1410;

interface IProps {
  main?: React.ReactNode;
  drawer?: React.ReactNode;
}

const useStyles = makeStyles<Theme, IProps>((theme) => ({
  grow: {
    flexGrow: 1,
  },
  root: {
    display: 'flex',
  },
  menuButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.up(responsiveBound)]: {
      display: 'none',
    },
  },
  sectionDesktop: {
    display: 'none',
    [theme.breakpoints.up(responsiveBound)]: {
      display: 'flex',
    },
  },
  sectionMobile: {
    display: 'flex',
    [theme.breakpoints.up(responsiveBound)]: {
      display: 'none',
    },
  },
  appBar: {
    [theme.breakpoints.up(responsiveBound)]: {
      width: ({ drawer }) => `calc(100% - ${drawer ? drawerWidth : 0}px)`,
      marginLeft: ({ drawer }) => (drawer ? drawerWidth : 0),
    },
  },
  drawer: {
    [theme.breakpoints.up(responsiveBound)]: {
      width: drawerWidth,
      flexShrink: 0,
    },
  },
  drawerPaper: {
    width: drawerWidth,
    padding: '0px 8px',
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    width: '100%',
  },
  toolbar: theme.mixins.toolbar,
  toolbarMobile: {
    minHeight: '104px',
  },
  keepitalButton: {
    backgroundColor: '#fff',
    borderRadius: '50px',
    fontWeight: 600,
    display: 'none',
    color: '#000',
    '&:hover': {
      backgroundColor: '#fff',
    },
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
  },
  subscribeButtonDrawer: {
    backgroundColor: theme.palette.mode === 'light' ? '#383838' : '#fff',
    borderRadius: '50px',
    fontWeight: 600,
    display: 'flex',
    color: theme.palette.mode === 'light' ? '#fff' : '#000',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'light' ? '#383838' : '#fff',
    },
  },
  guideButton: {
    color: '#fff',
  },
}));

export default function DefaultLayout(props: IProps) {
  const { main, drawer } = props;
  const classes = useStyles(props);
  const { t } = useTranslation();
  const { path } = useRoutes();
  const { company, setCompany } = useContext(Context);
  const isMinWidth500 = useMediaQuery('(min-width:500px)');
  const theme = useTheme();
  const isSmallMobileView = useMediaQuery(theme.breakpoints.down('sm'));
  const isUpMdView = useMediaQuery(theme.breakpoints.up('md'));
  const isMinWidthResponsiveBound = useMediaQuery(
    `(min-width:${responsiveBound}px)`
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isRunJoyride, setIsRunJoyride] = useState<boolean>(false);
  const [
    isHeaderAccountButtonOpen,
    setIsHeaderAccountButtonOpen,
  ] = useState<boolean>(false);
  const { routeName, params, setParams, history } = useRouter();
  const { path: keepitalPath } = useKeepitalRoutes();
  const currentApp = routeName.split('.')[0];

  const container =
    window !== undefined ? () => window.document.body : undefined;
  const trigger = useScrollTrigger();

  useEffect(() => {
    setCompany(params.companyID);
  }, [params.companyID]);

  useEffect(() => {
    if (routeName !== 'messenger.chatroom') {
      if (company && params.companyID !== company._id) {
        setParams({
          ...params,
          companyID: company._id,
        });

        if (currentApp) {
          if (currentApp === 'messenger') {
            history.push(
              path(`${currentApp}.chatrooms`, {
                companyID: company._id,
              })
            );
          } else {
            history.push(
              path(`${currentApp}.dashboard`, {
                companyID: company._id,
              })
            );
          }
        }
      }
    }
  }, [JSON.stringify(company)]);

  if (!company) {
    return null;
  }

  return (
    <div className={classes.root}>
      <Joyride
        steps={[
          {
            target: '.default-layout-header-notification-button-joyride',
            content: t(
              'access_your_notifications_you_can_use_it_to_see_what_notifications_you_have_received'
            ),
            disableBeacon: true,
          },
          {
            target: '.default-layout-header-app-button-joyride',
            content: t('switch_between_different_apps_on_keepital'),
            disableBeacon: true,
          },
          {
            target: '.default-layout-header-company-button-joyride',
            content: t(
              'switch_between_different_companies_that_you_have_access_to'
            ),
            disableBeacon: true,
          },
          {
            target: '.default-layout-header-keepital-button-joyride',
            content: t(
              'go_to_the_keepital_homepage_you_can_use_it_to_navigate_to_the_main_page_of_the_website'
            ),
            disableBeacon: true,
            onReady: async () => {
              if (!isHeaderAccountButtonOpen && !isMinWidthResponsiveBound) {
                setIsHeaderAccountButtonOpen(true);
              }
            },
            delay: 400,
          },
          {
            target:
              '.default-layout-header-account-button-switch-theme-button-joyride',
            content: t('switch_between_a_light_mode_and_a_dark_mode_theme'),
            disableBeacon: true,
            onReady: async () => {
              if (!isHeaderAccountButtonOpen) {
                setIsHeaderAccountButtonOpen(true);
              }
            },
            delay: 400,
          },
        ]}
        isRun={isRunJoyride}
        onRunEnd={() => {
          setIsRunJoyride(false);
        }}
        isContinuous
        showProgress
        showSkipButton
        disableScrolling
      />
      <Slide appear={false} direction="down" in={!trigger}>
        <AppBar position="fixed" className={classes.appBar}>
          <Toolbar>
            {drawer && (
              <IconButton
                edge="start"
                onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                className={classes.menuButton}
                color="inherit"
                aria-label="open drawer"
                size="large"
              >
                <MenuIcon />
              </IconButton>
            )}
            {isUpMdView && (
              <Button
                className={classNames(
                  classes.keepitalButton,
                  'default-layout-header-keepital-button-joyride'
                )}
                variant="contained"
                component="a"
                target="_blank"
                href={keepitalPath('home')}
                startIcon={<KeepitalKeepitalAppSvg height={24} width={24} />}
              >
                {t('keepital')}
              </Button>
            )}
            {!isSmallMobileView && (
              <IconButton
                onClick={async () => {
                  setIsRunJoyride(true);
                }}
                size="large"
                className={classes.guideButton}
              >
                <HelpOutlineIcon />
              </IconButton>
            )}
            <div className={classes.grow} />
            {isMinWidthResponsiveBound && (
              <Grid
                item
                xs={10}
                container
                spacing={2}
                justifyContent="flex-end"
                alignItems="center"
                className={classes.sectionDesktop}
              >
                <Grid item>
                  <HeaderCountryButton />
                </Grid>
                <Grid item>
                  <HeaderLanguageButton />
                </Grid>
                <Grid item>
                  <HeaderCurrencyButton />
                </Grid>
                <Grid item>
                  <HeaderNotificationButton />
                </Grid>
                <Grid item>
                  <HeaderAppButton />
                </Grid>
                <Grid item>
                  <HeaderCompanyButton />
                </Grid>
                <Grid item>
                  <HeaderAccountButton
                    isOpen={isHeaderAccountButtonOpen}
                    onOpen={(isOpen) => setIsHeaderAccountButtonOpen(isOpen)}
                  />
                </Grid>
              </Grid>
            )}
            {!isMinWidthResponsiveBound && (
              <Grid
                justifyContent="flex-end"
                item
                container
                spacing={1}
                alignItems="center"
                className={classes.sectionMobile}
              >
                {!isSmallMobileView && (
                  <>
                    <Grid item>
                      <HeaderCountryButton />
                    </Grid>
                    <Grid item>
                      <HeaderLanguageButton />
                    </Grid>
                    <Grid item>
                      <HeaderCurrencyButton />
                    </Grid>
                  </>
                )}
                <Grid item>
                  <HeaderNotificationButton />
                </Grid>
                <Grid item>
                  <HeaderAppButton iconOnly />
                </Grid>
                <Grid item>
                  <HeaderCompanyButton hideCompanyName hideDropdownIcon />
                </Grid>
                <Grid item>
                  <HeaderAccountButton
                    avatarOnly
                    isOpen={isHeaderAccountButtonOpen}
                    onOpen={(isOpen) => setIsHeaderAccountButtonOpen(isOpen)}
                  />
                </Grid>
              </Grid>
            )}
          </Toolbar>
        </AppBar>
      </Slide>
      {drawer && (
        <nav className={classes.drawer}>
          {!isMinWidthResponsiveBound ? (
            <Drawer
              container={container}
              variant="temporary"
              anchor="left"
              open={isDrawerOpen}
              onClose={() => setIsDrawerOpen(false)}
              classes={{
                paper: classes.drawerPaper,
              }}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile.
              }}
            >
              <div>
                <DrawerHeader app={currentApp} />
                <Divider />
                {drawer}
                {company.planData &&
                  company.planData.subscription &&
                  company.planData.subscription.packageData &&
                  company.planData.subscription.packageData.type ===
                    KEEPITAL_SUBSCRIPTION_PACKAGE.TYPE.FREE && (
                    <>
                      <Button
                        className={classes.subscribeButtonDrawer}
                        variant="contained"
                        component={Link}
                        to={path('plan.subscriptions', {
                          companyID: company._id,
                        })}
                        startIcon={<PaymentIcon />}
                      >
                        {t('upgrade')} {t('subscription')}
                      </Button>
                    </>
                  )}
              </div>
            </Drawer>
          ) : (
            <Drawer
              classes={{
                paper: classes.drawerPaper,
              }}
              variant="permanent"
              open
            >
              <div>
                <DrawerHeader app={currentApp} />
                <Divider />
                {drawer}
                {company.planData &&
                  company.planData.subscription &&
                  company.planData.subscription.packageData &&
                  company.planData.subscription.packageData.type ===
                    KEEPITAL_SUBSCRIPTION_PACKAGE.TYPE.FREE && (
                    <>
                      <Button
                        className={classes.subscribeButtonDrawer}
                        variant="contained"
                        component={Link}
                        to={path('plan.subscriptions', {
                          companyID: company._id,
                        })}
                        startIcon={<PaymentIcon />}
                      >
                        {t('upgrade')} {t('subscription')}
                      </Button>
                    </>
                  )}
              </div>
            </Drawer>
          )}
        </nav>
      )}
      <main className={classes.content}>
        <div
          className={isMinWidth500 ? classes.toolbar : classes.toolbarMobile}
        />
        {main}
      </main>
    </div>
  );
}
